import * as React from 'react';
import * as _ from 'underscore';
import * as $ from 'jquery';

import { IGlob } from '../../../pdc-platform/src/ts/Glob';
import { ISize } from '../ts/Interfaces';
import { IValuesCollection } from '../ts/appTheme';

declare var require;
declare var CKEDITOR;

const CKEditorRef = require('../../../_common/libs/bower_components/ckeditor/ckeditor');
const deepmerge = require('../../../_common/libs/deepmerge');

const HEIGHT_PADDING = 10;

export interface ICKEditFieldPropsBaseOptions {
    editorConfig?: {
        size?: ISize;
        height?: number;
        width?: number;
        extraPlugins?;
    };
    size?: ISize;
    themeObj?: IValuesCollection;
    dependencies?: { [name: string]: any };
    fillContainer?: boolean;
    readOnly?: boolean;
    startupFocus?: boolean; //IL: for DataEdit
    tabID: number;
    parentTabID?: number;
    inHtmlContainer?: boolean;
    allowNull?: boolean;
    dialogOffsetTop?: number;
    widthGap?: number;
}

export interface ICKEditFieldPropsBase<TValue> {
    value?: TValue;
    options?: ICKEditFieldPropsBaseOptions;
    parentTheme?; // Is not used.
    editorConfig?: any;
    editorConfigOverride?: any;
    size?: ISize;
    fillContainer?: boolean;
    dependencies?: { [name: string]: any };
    readOnly?: boolean;
    allowNull?: boolean;
    glob?: IGlob;

    onChange?(value: TValue): void;
    onResize?(value: TValue): void;
    onInstanceReady?(): void;
}

interface ICKEditFieldProps extends ICKEditFieldPropsBase<any> {
    isFullScreen?: boolean;
    themeObj?: IValuesCollection;
    dialogOffsetTop?: number;
    containerOffsetTop?: number;

    onExitFullScreen?(value: string): void;
}

export default class CKEditField extends React.Component<ICKEditFieldProps, {}> {
    private references: {
        textarea?: HTMLTextAreaElement;
    } = {};

    private editor: any;

    public getEditor = () => {
        return this.editor;
    };

    //EVG 20.05.2024. This is not in the state because of CKEditor keeps its own value.
    // So, there is no need to refresh by changing state.
    private currentValue: string;

    constructor(props) {
        super(props);

        this.currentValue = this.props.value;
    }

    handleEditorChange = (e) => {
        const data = e.editor.getData();

        this.handleOnChange(data);
    };

    handleOnResize = (editor, size: ISize) => {
        if (editor == null || size == null || (size.height == null && size.width == null)) {
            return;
        }
        //LL: 28.12.23: bug: status = 'unloaded' when Config of HTML Widget. Must be 'ready'
        if (editor.status !== 'ready') {
            return;
        }

        editor.resize(size.width, size.height);
    };

    handleOnParentResize = () => {
        if (typeof this.props.onResize === 'function') {
            this.props.onResize(this.editor.container.getClientRect().width);
        }
    };

    getDialogOffsetTop = () => {
        let result = this.props.dialogOffsetTop || this.props.options?.dialogOffsetTop;

        if (result > 16) {
            result -= 16;
        } else {
            result = 0;
        }

        return result;
    };

    getWidthGap = () => {
        return this.props.options?.widthGap ?? 18;
    };

    handleOnContainerResize = (eventName, dialogHeight: number) => {
        const dialogOffsetTop = this.getDialogOffsetTop();
        const widthGap = this.getWidthGap() - 4;

        this.processContainerResize(dialogHeight, widthGap, dialogOffsetTop);
    };

    processContainerResize = (
        dialogHeight: number,
        widthGap: number,
        dialogOffsetTop: number,
        containerOffsetTop?: number
    ) => {
        if (this.props.fillContainer || this.props.options.fillContainer) {
            const element = this.references.textarea;
            const parent = $(element).parent().parent();

            if (parent != null) {
                const width = $(parent).offsetParent().width() - widthGap;

                let height: number;

                if (dialogHeight != null) {
                    height = dialogHeight - (dialogOffsetTop || element.parentElement.offsetTop || 0) - HEIGHT_PADDING;
                } else {
                    height =
                        $(parent).offsetParent().height() - (containerOffsetTop ?? dialogOffsetTop) - HEIGHT_PADDING;
                }

                this.handleOnResize(this.editor, { width, height });
            }
        }
    };

    handleOnEditorInstanceReady = (e) => {
        const frame = $(e.editor.container.$).find('.cke_wysiwyg_frame');
        const options = this.props.options;

        if (frame) {
            frame.attr('title', '');
        }

        if (this.props.isFullScreen) {
            this.editor.resize($(window).width(), $(window).height());
            return;
        }

        if (this.props.fillContainer || options?.fillContainer) {
            const initialSize = this.props.size || options?.size;
            const dialogOffsetTop = this.getDialogOffsetTop();
            const widthGap = this.getWidthGap();

            if (initialSize != null && typeof initialSize.height === 'number') {
                this.processContainerResize(initialSize.height, widthGap, dialogOffsetTop);
            } else {
                this.processContainerResize(undefined, widthGap, dialogOffsetTop, this.props.containerOffsetTop);
            }
        } else {
            let size = this.props.size;

            if (options != null) {
                const editorConfig = options.editorConfig;

                if (editorConfig != null) {
                    if (editorConfig.size != null) {
                        size = editorConfig.size;
                    } else {
                        if (size == null) {
                            size = {};
                        }

                        if (editorConfig.height != null) {
                            size.height = editorConfig.height;
                        }

                        if (editorConfig.width != null) {
                            size.width = editorConfig.width;
                        }
                    }
                }
            }

            this.handleOnResize(this.editor, size);
        }

        this.editor.on('resize', this.handleOnParentResize);

        if (typeof this.props.onInstanceReady === 'function') {
            this.props.onInstanceReady();
        }
    };

    enterFullScreen = () => {
        const newProps: ICKEditFieldProps = _.omit(this.props, 'size');
        const currentValue = this.editor.getData();

        newProps.fillContainer = true;
        newProps.value = currentValue;

        this.props.glob.DialogActions.createDialogFullScreen(
            (vals) => {
                this.editor.setData(vals);

                if (typeof this.props.onChange === 'function') {
                    this.props.onChange(vals);
                }
            },
            {
                Component: CKEditField,
                props: newProps
            }
        );
    };

    exitFullScreen = () => {
        const currentValue = this.editor.getData();

        if (typeof this.props.onExitFullScreen === 'function') {
            this.props.onExitFullScreen(currentValue);
        }
    };

    attachDependencies(editor, dependencies) {
        if (editor == null || dependencies == null) {
            return;
        }

        _.reduce(
            Object.keys(dependencies),
            (editor, key: string) => {
                editor[key] = dependencies[key];

                return editor;
            },
            editor
        );
    }

    detachDependencies(editor, dependencies) {
        if (editor == null || dependencies == null) {
            return;
        }

        _.reduce(
            Object.keys(dependencies),
            (editor, key: string) => {
                delete editor[key];

                return editor;
            },
            editor
        );
    }

    componentDidMount() {
        const element = this.references.textarea;
        const options = this.props.options || ({} as ICKEditFieldPropsBaseOptions);
        const config = options.editorConfig;
        const theme = this.props.themeObj || options?.themeObj;
        const readOnly = this.props.readOnly || options?.readOnly;
        const inHtmlContainer = options?.inHtmlContainer;

        let extraPlugins = [
            'image2',
            'html5video',
            'widget',
            'widgetselection',
            'clipboard',
            'lineutils',
            'pdc-maximize'
        ];

        if (this.props.editorConfig != null && this.props.editorConfig.extraPlugins != null) {
            extraPlugins = extraPlugins.concat(this.props.editorConfig.extraPlugins.split(','));
        }

        if (config != null && config.extraPlugins != null) {
            extraPlugins = extraPlugins.concat(config.extraPlugins.split(','));
        }

        extraPlugins = _.uniq(extraPlugins);

        const fillContainer = this.props.fillContainer || options?.fillContainer;

        let { height, width } = this.props.size || options.size || {};

        if (height == null) {
            height = fillContainer ? 'full' : 600;
        }

        if (width == null) {
            width = fillContainer ? 'full' : 1024;
        }

        const editorConfig = deepmerge(
            {
                on: {
                    instanceReady: this.handleOnEditorInstanceReady,
                    change: _.debounce(this.handleEditorChange, 300),
                    key: _.debounce(this.handleEditorChange, 300)
                },
                startupFocus: options.startupFocus != null ? options.startupFocus : true,
                toolbar: [
                    {
                        name: 'basicstyles',
                        items: ['Bold', 'Italic', 'Underline', '-', 'RemoveFormat']
                    },
                    { name: 'colors', items: ['TextColor', 'BGColor'] },
                    {
                        name: 'styles',
                        items: ['FontSize', 'Styles', 'Format', 'Font']
                    },
                    {
                        name: 'paragraph',
                        items: [
                            'NumberedList',
                            'BulletedList',
                            '-',
                            'Outdent',
                            'Indent',
                            '-',
                            'Blockquote',
                            '-',
                            'JustifyLeft',
                            'JustifyCenter',
                            'JustifyRight',
                            'JustifyBlock',
                            '-',
                            'Language'
                        ]
                    },
                    {
                        name: 'insert',
                        items: ['Image', 'Table', 'HorizontalRule', 'PageBreak', 'Iframe', 'Smiley', 'html5video']
                    },
                    { name: 'links', items: ['Link', 'Unlink', 'Anchor'] },
                    { name: 'paragraph', items: ['CreateDiv'] },
                    {
                        name: 'editing',
                        items: ['Find', 'Replace', '-', 'Scayt']
                    },
                    { name: 'document', items: ['Source'] },
                    { name: 'tools', items: ['ShowBlocks', 'pdc-maximize'] },
                    { name: 'about', items: ['About'] }
                ],
                extraPlugins: extraPlugins.join(','),
                parentTabID: options?.parentTabID,
                allowedContent: {
                    $1: {
                        // allow All
                        elements: CKEDITOR.dtd,
                        attributes: true,
                        styles: true,
                        classes: true
                    }
                },
                disallowedContent: 'script; *[on*]',
                //extraAllowedContent: 'a[!href, download]; style; svg; use;',
                toolbarCanCollapse: false,
                language_list: ['en:English', 'da:Danish'],
                resize_dir: 'both', //Not 'both' by Taras 6 version: Allow horizontal resizing in CKEditField
                height: height,
                width: width,
                theme: theme,
                readOnly: readOnly
            },

            deepmerge(this.props.editorConfig || {}, config || {})
        );

        _.extend(editorConfig, this.props.editorConfigOverride);

        this.editor = CKEDITOR.replace(element, editorConfig);

        this.attachDependencies(this.editor, this.props.dependencies || options.dependencies);

        this.editor.isFullScreen = this.props.isFullScreen;
        this.editor.enterFullScreen = this.enterFullScreen;
        this.editor.exitFullScreen = this.exitFullScreen;
        this.editor.inHtmlContainer = inHtmlContainer;

        $(element).on('container-resize', this.handleOnContainerResize);

        CKEDITOR.on('dialogDefinition', function (ev) {
            function contains(elements, val) {
                let result = false;

                _.map(elements, function (el: any) {
                    if (el.id == val) {
                        result = true;
                        return;
                    }
                });
                return result;
            }

            const dialogName = ev.data.name;
            const dialogDefinition = ev.data.definition;

            if (dialogName === 'link') {
                dialogDefinition.onFocus = function () {
                    const targetField = this.getContentElement('target', 'linkTargetType');
                    const options = targetField.getInputElement().$.options;

                    let index = 0;

                    while (index < options.length) {
                        const option = options[index];

                        if (option.value !== 'popup' && option.value !== '_blank' && option.value !== '_self') {
                            targetField.remove(index);
                        } else {
                            index++;
                        }
                    }
                };
            }

            if (dialogName === 'image' || dialogName === 'link' || dialogName === 'image2') {
                const upload = dialogDefinition.getContents('Upload') || dialogDefinition.getContents('upload');

                if (upload) {
                    if (!contains(upload.elements, 'prompt')) {
                        upload.elements.push({
                            type: 'html',
                            id: 'prompt',
                            html: '<h3 style="color: red;" >Note: you MUST press "Send it to the Server" before pressing OK button. </h3>'
                        });
                    }
                }
            }
        });
    }

    componentWillUnmount() {
        if (this.editor) {
            this.detachDependencies(this.editor, this.props.dependencies);
            this.editor.destroy();

            delete this.editor.isFullScreen;
            delete this.editor.enterFullScreen;
            delete this.editor.exitFullScreen;
            delete this.editor;
        }

        $(this.references.textarea).off('container-resize', this.handleOnContainerResize);
    }

    //EVG 20.05.2024. When value is changed outside the component CKEditor keeps showing its own value.
    // To apply the new value, it should be set to CKEditor expicitly.
    componentDidUpdate(prevProps, prevState) {
        if (this.props.value !== this.currentValue) {
            this.currentValue = this.props.value;
            this.editor.setData(this.currentValue || '');
        }

        if (
            this.props.size != null &&
            prevProps.size != null &&
            (this.props.size.width !== prevProps.size.width || this.props.size.height !== prevProps.size.height)
        ) {
            this.handleOnResize(this.editor, this.props.size);
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (nextProps.value !== this.currentValue) {
            return true;
        }

        if (nextProps.size !== this.props.size) {
            return true;
        }

        return false;
    }

    handleOnChange = (value) => {
        if (this.currentValue === value) {
            return;
        }

        //EVG 04.07.2024: See Issue 86032. CKEdit adds \n to the value's end. It does a false calling of onChange.
        if (typeof value === 'string' && value.length > 0) {
            const match = /\r|\n$/.exec(value);

            if (match) {
                if (value.length === 1) {
                    value = '';
                } else {
                    value = value.substring(0, value.length - 1);
                }

                if (this.currentValue === value) {
                    return;
                }
            }
        }

        //console.log('CKEdit is changed. New Value: ' + JSON.stringify(value));
        this.currentValue = value;

        const allowNull = this.props.allowNull || this.props.options?.allowNull;

        if (allowNull && this.currentValue === '') {
            this.currentValue = null;
        }

        if (typeof this.props.onChange === 'function') {
            this.props.onChange(this.currentValue);
        }
    };

    render() {
        return (
            <div className="ckedit-field">
                <textarea
                    ref={(input) => {
                        this.references.textarea = input;
                    }}
                    className="form-control listen-container-resize"
                    value={this.props.value || ''}
                    onChange={this.handleOnChange}
                />
            </div>
        );
    }
}
